import { DEFAULT_REGION_ID } from "@/atoms/activeRegion";
import type { HomeSpot } from "@/atoms/homeRegion";
import type { RegionConfig } from "@/lib/regions";
import { coldStartFloridaHomePath, readLastViewedRegionId } from "@/lib/floridaHome";
import { getSpotPageByRegionBreak } from "@/lib/spotPages";
import { hasSeenWelcome } from "@/lib/welcomeFlag";
import { usePageRedirect } from "@/hooks/usePageRedirect";

interface HomeNavigationOptions {
  requestedRegionId: string | null;
  requestedCameraId: string | null;
  homeSpot: HomeSpot | null;
  regionsData: Record<string, RegionConfig> | undefined;
  isCanonicalSpotPage: boolean;
  /** Session region. Cold-start Flagler routing only applies at the fallback. */
  activeRegionId?: string;
}

export function useHomeNavigation({
  requestedRegionId,
  requestedCameraId,
  homeSpot,
  regionsData,
  isCanonicalSpotPage,
  activeRegionId = DEFAULT_REGION_ID,
}: HomeNavigationOptions) {
  let destination: string | null = null;
  let pending = false;

  if (!requestedRegionId) {
    // Welcome takes priority over a saved home; only one redirect owns this URL.
    if (!hasSeenWelcome()) {
      destination = "/welcome";
    } else if (homeSpot) {
      pending = !regionsData;
      if (regionsData?.[homeSpot.regionId]) {
        const region = encodeURIComponent(homeSpot.regionId);
        const camera = homeSpot.cameraId
          ? `/${encodeURIComponent(homeSpot.cameraId)}`
          : "";
        destination = homeSpot.preferMultiCam
          ? `/multicam/${region}`
          : `/spot/${region}${camera}`;
      }
    } else {
      destination = coldStartFloridaHomePath(activeRegionId, readLastViewedRegionId());
    }
  } else if (!isCanonicalSpotPage && requestedCameraId) {
    destination =
      getSpotPageByRegionBreak(requestedRegionId, requestedCameraId)?.path ?? null;
  }

  return usePageRedirect(destination, pending);
}
